import React, { useState } from 'react';
import api from '../../services/api';
import { LoadingSpinner } from '../common/LoadingSpinner';

// Set a first password (OAuth accounts) or change an existing one. OAuth
// accounts have no password, so the desktop/mobile apps (email+password only)
// can't sign them in until one is set here.
export const PasswordSettings: React.FC<{ email: string; hasPassword: boolean; onChanged?: () => void }> = ({
  email,
  hasPassword,
  onChanged,
}) => {
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setOk('');
    if (pw.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (pw !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      if (hasPassword) {
        await api.put('/api/auth/change-password', { current_password: current, new_password: pw });
        setOk('Password updated.');
      } else {
        await api.post('/api/auth/set-password', { new_password: pw });
        setOk('Password set — you can now sign in to the apps with your email and this password.');
      }
      setCurrent('');
      setPw('');
      setConfirm('');
      onChanged?.();
    } catch (e2: any) {
      setErr(e2.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-panel" style={{ marginTop: 0 }}>
      <h2>{hasPassword ? 'Change password' : 'Set a password'}</h2>
      {!hasPassword && (
        <p className="auth-note" style={{ textAlign: 'left', margin: '0 0 14px' }}>
          Your account uses Google or Microsoft sign-in. Set a password to sign in to the desktop and
          mobile apps — they don't support Google sign-in yet.
        </p>
      )}
      <form onSubmit={submit}>
        {/* Hidden username field: lets password managers associate the saved
            credential with this account, and satisfies the browser's
            "password form should have a username field" accessibility hint. */}
        <input type="text" name="username" autoComplete="username" value={email} readOnly
          aria-hidden="true" tabIndex={-1} style={{ display: 'none' }} />
        {err && <div className="auth-error" role="alert">{err}</div>}
        {ok && (
          <div className="auth-note" style={{ textAlign: 'left', color: 'var(--teal-bright)', margin: '0 0 12px' }}>
            {ok}
          </div>
        )}
        {hasPassword && (
          <div className="auth-field">
            <label className="auth-label" htmlFor="cur-pw">Current password</label>
            <input id="cur-pw" className="auth-input" type="password" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
        )}
        <div className="auth-field">
          <label className="auth-label" htmlFor="new-pw">New password</label>
          <input id="new-pw" className="auth-input" type="password" autoComplete="new-password"
            placeholder="At least 8 characters" value={pw} onChange={(e) => setPw(e.target.value)} required />
        </div>
        <div className="auth-field">
          <label className="auth-label" htmlFor="conf-pw">Confirm new password</label>
          <input id="conf-pw" className="auth-input" type="password" autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        <button type="submit" className="auth-btn" disabled={loading} style={{ marginTop: 6 }}>
          {loading ? <LoadingSpinner size="sm" /> : hasPassword ? 'Update password' : 'Set password'}
        </button>
      </form>
    </div>
  );
};
